import * as Business     from '../business';
import * as Contacts     from '../contacts';
import * as errors       from '../util/errors';
import * as Forms        from '../components/forms';
import * as User         from '../user';
import { compose }       from 'redux';
import { connect }       from 'react-redux';
import { getFileURL }    from '../util/file_entry';
import { TwitterPicker } from 'react-color';
import { uploadAvatar }  from '../util/file_uploader';
import classnames        from 'classnames';
import copyToClipboard   from '../util/copy_to_clipboard';
import EditableLineItem  from '../components/editable_line_item';
import FetchFromServer   from '../components/fetch_from_server';
import Link              from '../components/link';
import Navbar            from '../components/navbar';
import pluralize         from '../util/pluralize';
import React             from 'react';
import restrictAccess    from '../components/restrict_access';
import SwitchControl     from '../components/switch_control';
import taptic            from '../util/taptic';
import UploadImage       from '../components/upload_image';


export default connect(
  (state, { businessID }) => ({
    businessID,
    organization: Business.getBusinessOrganization(state, businessID),
    businessName: Business.getBusinessName(state, businessID),
    defaults:     Business.getDefaults(state, businessID),
    settings:     Business.getSettings(state, businessID),
    contact:      Business.getContact(state, businessID),
    customers:    Contacts.getLeadsAddedDaysAgo(state, businessID, 30),
    statuses:     [
      Contacts.getContactsStatuses(state, businessID),
      Business.getBusinessStatuses(state, businessID)
    ],
    isManager: User.isManager(state, businessID)
  }),
  (dispatch, { businessID }) => ({
    load: () => {
      dispatch(Business.loadBusiness(businessID));
      dispatch(Contacts.readContacts(businessID));
    },
    updateBusinessSettings: compose(dispatch, Business.updateBusinessSettings, settings => ({ businessID, settings })),
    save:                   compose(dispatch, Business.setChat, params => ({ businessID, ...params })),
    showToast:              compose(dispatch, errors.showToastError),
    upload:                 compose(dispatch, uploadAvatar)
  })
)(restrictAccess(WebchatPage, ({ isManager }) => isManager));


function WebchatPage({ businessID, businessName, organization, contact, customers, settings, defaults, load, statuses, save, showToast, upload, updateBusinessSettings }) {
  return (
    <React.Fragment>
      <Navbar title="Web Chat"/>
      <FetchFromServer statuses={statuses} load={load} className={classnames('settings', 'webchat')}>
        {settings && defaults && customers && (
          <React.Fragment>
            <LeadCount customers={customers}/>
            <WebchatSettings
              businessID={businessID}
              businessName={businessName}
              organization={organization}
              showToast={showToast}
              defaults={defaults}
              settings={settings}
              contact={contact}
              save={save}
              upload={upload}
              updateBusinessSettings={updateBusinessSettings}
            />
          </React.Fragment>
        )}
      </FetchFromServer>
    </React.Fragment>
  );
}

function LeadCount({ customers }) {
  const leadCount = customers.length;
  return (
    <div className="lead-count">
      <h1>{leadCount}</h1>
      <h2>New {pluralize(leadCount, 'lead')} last 30 days</h2>
    </div>
  );
}

function WebchatSettings({ businessID, businessName, organization, contact, settings, defaults, save, showToast, upload, updateBusinessSettings }) {
  const orgHasMoreThanOneBusiness    = organization?.businessCount > 1;
  const orgHasLessThanFourBusinesses = organization?.businessCount <= 4;
  const showMultiLocationOption      = orgHasMoreThanOneBusiness && orgHasLessThanFourBusinesses;
  const showFolloUpAfterHours        = settings.workingHours.status === 'active';

  return (
    <React.Fragment>
      <div className="list inset">
        <ul>
          <EnableWebChatLineItem
            contact={contact}
            save={save}
            settings={settings}
          />

          {showMultiLocationOption &&
            <EnableMultiLocationLineItem
              businessName={businessName}
              organization={organization}
              save={save}
              settings={settings}
            />
          }

          <EditableUploadImageLineItem
            title="Avatar"
            description="Upload an image to use as Web Chat avatar."
            value={settings.chat.avatarURL}
            color={settings.chat.color}
            save={avatarURL => save({ avatarURL })}
            upload={upload}
            showToast={showToast}
          />

          <EditableColorPicker
            title="Color"
            description="Change Web Chat color to reflect your brand."
            value={settings.chat.color}
            defaultValue={defaults.chat.color}
            save={value => save({ color: value })}
            showToast={showToast}
          />


          <EditableLineItem
            title="Display Name"
            value={settings.chat.displayName}
            defaultValue={defaults.chat.displayName}
            save={value => save({ displayName: value })}
            showToast={showToast}
          />

          <EditableBusinessHoursLineItem
            settings={settings}
            save={updateBusinessSettings}
            showToast={showToast}
          />

          <EditableLineItem.ListItem
            title="Ask for zip code"
            content="Ask leads for their zip code, to see if they're in your service area."
            after={<SwitchControl checked={settings.chat.postalCodes.enabled} save={value => save({ postalCodesEnabled: value })}/>}
            showToast={showToast}
          />

          <EditableLineItem
            title="Web Chat greeting"
            value={settings.chat.greeting}
            defaultValue={defaults.chat.greeting}
            save={value => save({ greeting: value })}
            showToast={showToast}
          />

          <EditableLineItem
            title="Follow up message to new lead"
            value={settings.chat.followUp}
            defaultValue={defaults.chat.followUp}
            save={value => save({ followUp: value })}
            showToast={showToast}
          />

          {
            showFolloUpAfterHours && (
              <EditableLineItem
                title="Follow up message to new lead after business hours"
                value={settings.chat.followUpAfterHours}
                defaultValue={defaults.chat.followUpAfterHours}
                save={value => save({ followUpAfterHours: value })}
                showToast={showToast}
              />
            )
          }
        </ul>
      </div>

      <WebchatSetupInstructions
        businessID={businessID}
        showToast={showToast}
      />
    </React.Fragment>
  );
}

function EnableMultiLocationLineItem({ organization, businessName, settings, save }) {
  const isEnabled       = settings?.chat?.multiLocationEnabled ?? false;
  const enabledContent  = `We'll ask website visitors which location they're closest to, then route leads to the appropriate location within the ${organization.name} brand.`;
  const disabledContent = `We'll route all leads from your website to ${businessName}`;
  const content         = isEnabled ? enabledContent : disabledContent;

  return (
    <EditableLineItem.ListItem
      title="Use Web Chat for multiple locations"
      content={content}
      after={<SwitchControl checked={isEnabled} save={value => save({ multiLocationEnabled: value })}/>}
    />
  );
}

function EnableWebChatLineItem({ settings, contact, save }) {
  const isEnabled   = settings?.chat?.enabled;
  const websiteURL  = contact?.url;
  const description = isEnabled ?
    (
      <React.Fragment>
        Web Chat enabled. Make sure it&apos;s installed correctly on your website.
        &nbsp; {websiteURL && (
          <Link href={websiteURL} className="link" style={{ whiteSpace: 'nowrap' }} external>
            {websiteURL}
          </Link>
        )}
      </React.Fragment>
    ) :
    'Web Chat is currently disabled on your website.';

  return (
    <EditableLineItem.ListItem
      title="Enable Web Chat"
      content={description}
      after={<SwitchControl checked={isEnabled} save={value => save({ enabled: value })}/>}
    />
  );
}


function Preview({ url, color, blob, mimeType, nativeURL }) {
  if (nativeURL)
    return <img className="preview" src={getFileURL(nativeURL)}/>;
  else {
    const style = getStyleForPreview({ url, color, blob, mimeType });
    return <div className="preview" style={style}/>;
  }
}

function getStyleForPreview({ url, color, blob, mimeType }) {
  if (blob && mimeType)
    return { backgroundImage: `url(${URL.createObjectURL(blob)})` };

  if (url)
    return { backgroundImage: `url(${url})` };

  // Fallback, show color.
  return { backgroundColor: color };
}

function EditableColorPicker({ title, value, defaultValue, save, showToast, description }) {
  const styles = {
    default: {
      card: {
        boxShadow: 'none'
      },
      body: {
        padding: '0px'
      }
    }
  };

  const colors = [
    '#FF6900',
    '#FCB900',
    '#7BDCB5',
    '#00D084',
    '#8ED1FC',
    defaultValue,
    '#ABB8C3',
    '#EB144C',
    '#F78DA7',
    '#9900EF'
  ];

  return (
    <EditableLineItem
      value={value}
      defaultValue={defaultValue}
      save={save}
      showToast={showToast}
    >
      <EditableLineItem.Viewing>
        {({ handleStartEditing }) => (
          <EditableLineItem.ListItem
            media={<Preview color={value}/>}
            title={title}
            titleActions={<a onClick={handleStartEditing}>Edit</a>}
            content={description}
          />
        )}
      </EditableLineItem.Viewing>
      <EditableLineItem.Editing>
        {({ currentValue, handleCancel, handleChange, handleSave }) => (
          <EditableLineItem.ListItem
            shouldScrollIntoView
            media={<Preview color={currentValue}/>}
            title={title}
            content={
              <React.Fragment>
                {description}
                <TwitterPicker
                  className="color-picker"
                  color={currentValue}
                  colors={colors}
                  onChange={color => handleChange(color.hex)}
                  styles={styles}
                  triangle="hide"
                  width="100%"
                />
                <EditableLineItem.Buttons
                  handleCancel={handleCancel}
                  handleSave={handleSave}
                />
              </React.Fragment>
            }
          />
        )}
      </EditableLineItem.Editing>
    </EditableLineItem>
  );
}

function EditableBusinessHoursLineItem({ settings, save, showToast }) {
  const { workingHours }                          = settings;
  const [ isEditing, setIsEditing ]               = React.useState(false);
  const withoutStatus                             = { ...workingHours, status: undefined };
  const [ workingHoursForm, setWorkingHoursForm ] = React.useState(withoutStatus);
  const isEnabled                                 = workingHours.status === 'active';
  const description                               = isEnabled ? 'We’ll continue to capture lead information while you’re closed, and let leads know when you reopen.' : 'We’ll let leads know that you’ll be in touch shortly, regardless of the time of day.';
  const dayNames                                  = [ 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday' ];
  const hoursOptions                              = getDayHoursOptions();

  function updateDay({ dayName, newValue = {} }) {
    if (newValue.to && newValue.to <= workingHoursForm[dayName].from) {
      showToast(`Time should be after ${workingHoursForm[dayName].from}`);
      return;
    }

    if (newValue.from && newValue.from >= workingHoursForm[dayName].to) {
      showToast(`Time should be before ${workingHoursForm[dayName].to}`);
      return;
    }

    setWorkingHoursForm({
      ...workingHoursForm,
      [dayName]: {
        ...workingHoursForm[dayName],
        ...newValue
      }
    });
  }

  function onSubmit() {
    save({ workingHours: workingHoursForm });
    setIsEditing(false);
  }

  return (
    <div className="business-hours">
      <EditableLineItem.ListItem
        title="Enable business hours"
        titleActions={<a onClick={() => setIsEditing(!isEditing)}>Edit Hours</a>}
        content={description}
        after={<SwitchControl checked={isEnabled} save={enabled => {
          save({
            workingHours: {
              status: enabled ? 'active' : 'inactive'
            }
          });
        }}/>}
      />

      {isEditing && (
        <div className="working-hours-form">
          {dayNames.map(dayName => (
            <div key={dayName} className="days">
              <Forms.Checkbox
                name={dayName}
                label={dayName}
                checked={workingHoursForm[dayName].isOpen}
                onChange={({ target }) => updateDay({ dayName, newValue: { isOpen: target.checked } })}
              />

              <div className="times">
                <label htmlFor={`${dayName}-fromTime`}>From:</label>
                <Forms.Select
                  name={`${dayName}-fromTime`}
                  onChange={({ target }) => updateDay({ dayName, newValue: { from: target.value } })}
                  value={workingHoursForm[dayName].from}
                  options={hoursOptions}
                />
              </div>

              <div className="times">
                <label htmlFor={`${dayName}-toTime`}>To:</label>
                <Forms.Select
                  id={dayName}
                  name={`${dayName}-toTime`}
                  onChange={({ target }) => updateDay({ dayName, newValue: { to: target.value } })}
                  value={workingHoursForm[dayName].to}
                  options={hoursOptions}
                />
              </div>
            </div>
          ))}
          <EditableLineItem.Buttons
            handleCancel={() => setIsEditing(false)}
            handleSave={onSubmit}
          />
        </div>
      )}

    </div>
  );
}

function EditableUploadImageLineItem({ title, value, color, save, showToast, description, upload }) {

  return (
    <EditableLineItem
      value={value}
      defaultValue={null}
      save={save}
      showToast={showToast}
    >
      <EditableLineItem.Viewing>
        {({ handleStartEditing }) => (
          <EditableLineItem.ListItem
            media={<Preview url={value} color={color}/>}
            title={title}
            titleActions={<a onClick={handleStartEditing}>Edit</a>}
            content={description}
          />
        )}
      </EditableLineItem.Viewing>
      <EditableLineItem.Editing>
        {({ currentValue, handleCancel, handleChange, handleSave, handleUseDefault }) => (
          <UploadImageLineItem
            title={title}
            description={description}
            value={currentValue}
            color={color}
            handleCancel={handleCancel}
            handleChange={handleChange}
            handleUseDefault={handleUseDefault}
            handleSave={handleSave}
            upload={upload}
            showToast={showToast}
          />
        )}
      </EditableLineItem.Editing>
    </EditableLineItem>
  );
}

class UploadImageLineItem extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      blob:      null,
      mimeType:  null,
      nativeURL: null
    };

    this.handleCancel       = this.handleCancel.bind(this);
    this.handleError        = this.handleError.bind(this);
    this.handleFileSelected = this.handleFileSelected.bind(this);
    this.handleSave         = this.handleSave.bind(this);
    this.handleUseDefault   = this.handleUseDefault.bind(this);
    this.resetData          = this.resetData.bind(this);
  }

  async handleSave() {
    const { blob }     = this.state;
    const { mimeType } = this.state;
    const { upload }   = this.props;

    if (blob)  {
      const sourceUrl = await upload({ mimeType, blob });
      this.props.handleChange(sourceUrl);
    }

    await this.props.handleSave();
  }

  resetData() {
    this.setState({ blob: null, mimeType: null, nativeURL: null });
  }

  handleUseDefault() {
    this.resetData();
    this.props.handleChange(null);
    this.props.handleUseDefault();
  }

  handleCancel() {
    this.resetData();
    this.props.handleCancel();
  }


  handleError(error) {
    // FIXME: Until we upgrade babel to =>= 7, we can't check with instanceof
    // extends UnsupportedFileError.
    this.props.showToast(error.message);
  }

  handleFileSelected({ blob, mimeType, nativeURL }) {
    this.setState({ blob, mimeType, nativeURL });
  }


  render() {
    const { color }       = this.props;
    const { description } = this.props;
    const { title }       = this.props;
    const { value }       = this.props;

    return (
      <EditableLineItem.ListItem
        shouldScrollIntoView
        media={<Preview color={color} url={value} {...this.state}/>}
        title={title}
        content={
          <React.Fragment>
            {description}
            <UploadImage
              onSuccess={this.handleFileSelected}
              onError={this.handleError}
            />
            <EditableLineItem.Buttons
              handleCancel={this.props.handleCancel}
              handleUseDefault={this.handleUseDefault}
              useDefaultLabel="Delete"
              handleSave={this.handleSave}
            />
          </React.Fragment>
        }
      />
    );
  }
}


function WebchatSetupInstructions({ businessID, showToast }) {
  const setupHTML               = getSetupHTML(businessID);
  const contactFormEmailAddress = `consumer+${businessID}@broadly.com`;

  return (
    <React.Fragment>
      <div className="list inset">
        <ul>
          <li className="list-group-title">Install on website</li>
          <li>
            <div className="item-content">
              <div className="item-inner">
                <div className="item-subtitle">
                  Add Broadly Web Chat on your website.
                  Copy and paste this script into the head element of your website HTML.
                  <CopyableCodeBlock content={setupHTML} showToast={showToast}/>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </div>
      <div className="list inset">
        <ul>
          <li className="list-group-title">WordPress</li>
          <li>
            <div className="item-content">
              <div className="item-inner">
                <div className="item-subtitle">
                  <ol>
                    <li>
                      Install the&nbsp;
                      <Link href="https://wordpress.org/plugins/broadly" className="link" external>
                        Broadly WordPress plugin
                      </Link>.
                    </li>
                    <li>After activating the Broadly plugin, go to the Settings → Broadly Setup page.</li>
                    <li>
                      Enter your Broadly Account ID:
                      <CopyableCodeBlock content={businessID} showToast={showToast}/>
                    </li>
                    <li>Web Chat is instantly enabled on your website.</li>
                  </ol>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </div>
      <div className="list inset">
        <ul>
          <li className="list-group-title">Contact Form</li>
          <li>
            <div className="item-content">
              <div className="item-inner">
                <div className="item-subtitle">
                  You can also send leads from your contact form directly to the Broadly App.
                  <br/>
                  Update your contact form to use the following email address: &nbsp;
                  <CopyableCodeBlock content={contactFormEmailAddress} showToast={showToast}/>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </React.Fragment>
  );
}


function CopyableCodeBlock({ content, showToast }) {
  return (
    <React.Fragment>
      <code
        className="code-block code-block--copyable"
        style={{ cursor: 'pointer' }}
        onClick={copy}>{content}</code>
      <div className="code-block__copy">
        <a onClick={copy}>Copy</a>
      </div>
    </React.Fragment>
  );


  async function copy() {
    try {
      await copyToClipboard(content);
      taptic('success');
      showToast('Copied to clipboard.');
    } catch (error) {
      taptic('error');
      showToast('Failed to copy.');
    }
  }
}


function getSetupHTML(businessID) {
  return `<script>window.broadlyChat={id:"${businessID}"};</script><script src="https://chat.broadly.com/javascript/chat.js" async></script>`;
}

function getDayHoursOptions() {
  const hours = [];

  for (let i = 0; i <= 23; i++) {
    const [ fullHourWithLabel, halfHourWithLabel ] = getHoursWithLabels(i);
    hours.push(fullHourWithLabel);
    hours.push(halfHourWithLabel);
  }

  return hours;
}


function getHoursWithLabels(hour) {
  const fullHour = `${hour}:00`.padStart(5, '0');
  const halfHour = `${hour}:30`.padStart(5, '0');

  const [ fullHourLabel, halfHourLabel ] = getLabelsForHour(hour);

  return [
    [ fullHour, fullHourLabel ],
    [ halfHour, halfHourLabel ]
  ];
}


function getLabelsForHour(hour) {
  const isAM   = hour >= 0 && hour < 12;
  const isNoon = hour === 12;

  if (isAM)
    return [ `${hour}:00 am`, `${hour}:30 am` ];
  else if (isNoon)
    return [ '12:00 pm', '12:30 pm' ];
  else {
    const hourIn12HoursFormat = hour - 12;
    return [ `${hourIn12HoursFormat}:00 pm`, `${hourIn12HoursFormat}:30 pm` ];
  }
}
